import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { DomainFeedConversionRateDay } from '@/domain/biomass/types';
import type { Locale } from '@/utils/localeFormatters';

import { daypartColor } from '../daypartSeries';

// ---------------------------------------------------------------------------
// Feed conversion ratio — not overview-fed, but rendered on the same pages
// ---------------------------------------------------------------------------

export interface FeedConversionRatioChartSpecParams {
    days: DomainFeedConversionRateDay[];
    /** Already translated. */
    axisLabel: string;
    locale: Locale;
}

const FCR_SUGGESTED_MAX = 1.5;
const FCR_DECIMALS = 2;

/**
 * Biological feed conversion ratio, plotted as points.
 *
 * Non-positive and absent ratios become gaps: feed conversion cannot be zero or
 * negative, so the legacy chart dropped those points entirely rather than
 * plotting them. Reproduced here as `null`, which reads the same on the chart but
 * keeps the day on the axis.
 *
 * The legacy chart also passed `suggestedMin: 0.5` alongside `startYAxisAtZero`,
 * which overrode it; only the zero baseline is kept.
 */
export function buildFeedConversionRatioChartSpec(
    params: FeedConversionRatioChartSpecParams
): MetricChartSpec<'line'> {
    const { days, axisLabel, locale } = params;

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            {
                key: 'fcr',
                label: axisLabel,
                color: daypartColor('all'),
                showLine: false,
                data: days.map((day) => ({
                    day: day.day,
                    value: day.fcr !== null && day.fcr > 0 ? day.fcr : null,
                })),
            },
        ],
        left: {
            label: axisLabel,
            beginAtZero: true,
            suggestedMax: FCR_SUGGESTED_MAX,
            tickDecimals: FCR_DECIMALS,
            valueDecimals: FCR_DECIMALS,
        },
        locale,
    };
}
